import React from 'react';
import { translate } from 'react-i18next';
import CBMediaQuery from '../shared/CBMediaQuery';
import CBSpinner from '../shared/CBSpinner';
import { getStyle } from '../../utils/utils';
import NotificationCardHeader from './NotificationCardHeader';
import NotificationToggleRow from './NotificationToggleRow';
import { styles, inputStyle } from './notificationSettingsStyles';

const SEARCH_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 2;

// Fleet notifications of one organization: the company-wide master switch,
// its garages and the vehicle-level exceptions. A garage or vehicle row only
// exists when it differs from what it would inherit ("poikkeus").
class NotificationOrganizationCard extends React.Component {
  state = {
    search: '',
  };

  searchTimer = null;

  componentWillUnmount() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  }

  searchChanged = (event) => {
    const { organization, onSearch, onClearSearch } = this.props;
    const search = event.target.value;
    this.setState({ search });
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    if (search.trim().length < MIN_SEARCH_LENGTH) {
      onClearSearch();
      return;
    }
    this.searchTimer = setTimeout(() => onSearch(organization.id, search.trim()), SEARCH_DELAY_MS);
  };

  renderSearchResults(screenSize) {
    const { t, organization, vehicleSearch, onToggle, disabled } = this.props;
    const { search } = this.state;

    if (vehicleSearch.organizationId !== organization.id || search.trim().length < MIN_SEARCH_LENGTH) {
      return null;
    }
    if (vehicleSearch.loading && !vehicleSearch.results.length) {
      return <div style={getStyle(screenSize, styles, 'searchResults')}><CBSpinner /></div>;
    }
    if (!vehicleSearch.results.length) {
      return <p style={getStyle(screenSize, styles, 'emptyText')}>{t('noVehiclesFound')}</p>;
    }
    return (
      <div style={getStyle(screenSize, styles, 'searchResults')}>
        {vehicleSearch.results.map((vehicle, index) => (
          <NotificationToggleRow
            key={vehicle.id}
            first={index === 0}
            label={vehicle.registration_number || vehicle.name}
            hint={[vehicle.name, vehicle.garage_name].filter(part => part).join(' · ')}
            checked={vehicle.enabled}
            disabled={disabled}
            onChange={(checked) => {
              // The vehicle moves to the exception list; close the search.
              this.setState({ search: '' });
              onToggle('vehicle', vehicle.id, checked);
            }}
          />
        ))}
      </div>
    );
  }

  render() {
    const { t, organization, onToggle, disabled } = this.props;
    const { search } = this.state;
    const exceptionCount = organization.garages.filter(g => g.is_exception).length
      + organization.vehicle_exceptions.length;

    return (
      <CBMediaQuery>
        {screenSize => (
          <div style={getStyle(screenSize, styles, 'card')} data-testid={`organization-card-${organization.id}`}>
            <NotificationCardHeader
              icon='pi pi-building'
              title={organization.name}
              description={t('organizationDescription')}
            />

            <NotificationToggleRow
              master
              testId={`organization-switch-${organization.id}`}
              label={t('organizationMasterLabel')}
              hint={exceptionCount
                ? t('organizationMasterHintWithExceptions', { count: exceptionCount })
                : t('organizationMasterHint')}
              checked={organization.enabled}
              disabled={disabled}
              onChange={checked => onToggle('organization', organization.id, checked)}
            />

            {organization.garages.length > 0 && (
              <React.Fragment>
                <p style={getStyle(screenSize, styles, 'sectionTitle')}>{t('garagesTitle')}</p>
                {organization.garages.map((garage, index) => (
                  <NotificationToggleRow
                    key={garage.id}
                    first={index === 0}
                    testId={`garage-row-${garage.id}`}
                    label={garage.name}
                    badge={garage.is_exception ? t('exceptionBadge') : null}
                    hint={garage.is_exception ? t('garageExceptionHint') : null}
                    action={garage.is_exception
                      ? { label: t('resetException'), onClick: () => onToggle('garage', garage.id, null) }
                      : null}
                    checked={garage.enabled}
                    disabled={disabled}
                    onChange={checked => onToggle('garage', garage.id, checked)}
                  />
                ))}
              </React.Fragment>
            )}

            <p style={getStyle(screenSize, styles, 'sectionTitle')}>{t('vehicleExceptionsTitle')}</p>
            {organization.vehicle_exceptions.length === 0 && (
              <p style={getStyle(screenSize, styles, 'rowHint')}>{t('noVehicleExceptions')}</p>
            )}
            {organization.vehicle_exceptions.map((vehicle, index) => (
              <NotificationToggleRow
                key={vehicle.id}
                first={index === 0}
                testId={`vehicle-exception-${vehicle.id}`}
                label={vehicle.registration_number || vehicle.name}
                badge={t('exceptionBadge')}
                hint={[vehicle.name, vehicle.garage_name].filter(part => part).join(' · ')}
                action={{ label: t('resetException'), onClick: () => onToggle('vehicle', vehicle.id, null) }}
                checked={vehicle.enabled}
                disabled={disabled}
                onChange={checked => onToggle('vehicle', vehicle.id, checked)}
              />
            ))}

            <div style={{ marginTop: 12 }}>
              <input
                type='text'
                className='white-placeholder-input'
                style={inputStyle}
                value={search}
                placeholder={t('vehicleSearchPlaceholder')}
                aria-label={t('vehicleSearchPlaceholder')}
                onChange={this.searchChanged}
              />
              {this.renderSearchResults(screenSize)}
            </div>
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

export default translate('NotificationSettings')(NotificationOrganizationCard);
